/**
 * MusicDirector — intermittent, authored-file-only music.
 *
 * Music is NOT a continuous bed. The facade names the POOL that fits the context
 * (planet / flight / menu, or null). Within a pool:
 *
 *     enter context → wait `startGap` → play a random track → wait a random gap
 *     (`minGap…maxGap`, minutes) → play a different random track → …
 *
 * Tracks play through <audio> from a fully downloaded compressed Blob. This
 * prevents network underruns without keeping minutes of decoded PCM in memory.
 * Surface music has its own sampled PlaceMusicDirector on the same bus.
 *
 * Each track instance owns its own gain node, so starts/stops crossfade cleanly;
 * retired voices fade out and are reaped a few seconds later.
 */
import { MUSIC_POOLS, MUSIC_TRACKS, type MusicPool, type MusicTrackDef } from "./manifest";
import type { AudioEngine } from "./engine";

const FADE_IN = 3;
const FADE_OUT = 4;

type Phase = "idle" | "gap" | "playing";

interface Media {
  el: HTMLAudioElement;
  src: MediaElementAudioSourceNode;
  url: string;
}
interface Voice {
  id: string;
  gain: GainNode;
  media: Media;
}
interface Retiring {
  gain: GainNode;
  until: number;
  media: Media;
}

export class MusicDirector {
  private pool: string | null = null;
  private phase: Phase = "idle";
  private timer = 0; // seconds left in the current phase (Infinity = ended-driven)
  private last: string | null = null;
  private active: Voice | null = null;
  private retiring: Retiring[] = [];
  private gen = 0; // cancels async buffer starts / stream errors on changes
  private download: { id: string; abort: AbortController; ready: Promise<Blob> } | null = null;
  private next: string | null = null;

  constructor(private engine: AudioEngine) {}

  /** The currently-audible track id, or null during a gap / silence. */
  get playing(): string | null {
    return this.active?.id ?? null;
  }

  get currentPool(): string | null {
    return this.pool;
  }

  /** Select the active pool for the context (null = no music here). */
  setPool(pool: string | null): void {
    if (pool === this.pool) return;
    this.pool = pool;
    if (!pool) {
      this.gen++;
      this.clearDownload();
      this.retireActive();
      this.phase = "idle";
      this.timer = 0;
      return;
    }
    const poolDef = MUSIC_POOLS[pool];
    if (!poolDef || poolDef.tracks.length === 0) {
      this.gen++;
      this.clearDownload();
      this.retireActive();
      this.phase = "idle";
      this.timer = 0;
      return;
    }
    // Entering from silence: schedule the first track after startGap. If a track
    // is already playing, let it finish; the next pick uses the new pool.
    if (this.phase === "idle") {
      this.phase = "gap";
      this.timer = MUSIC_POOLS[pool]?.startGap ?? 10;
    }
    this.next = pickDifferent(poolDef.tracks, this.last);
    // Use the quiet entrance gap to download, instead of starting the request
    // at the same instant as playback. Failed downloads are retried on demand.
    void this.prepare(this.next).catch(() => {});
  }

  update(dt: number): void {
    this.reap();
    if (!this.pool || this.phase === "idle") return;
    if (this.timer === Infinity) return; // streamed track: ends via the element
    this.timer -= dt;
    if (this.timer > 0) return;
    if (this.phase === "gap") this.startTrack();
    else this.endTrack();
  }

  /** DEV/force: skip the gap and start a track from the current pool now. */
  playNow(): void {
    if (this.pool) this.startTrack();
  }

  private startTrack(): void {
    const pool = this.pool ? MUSIC_POOLS[this.pool] : null;
    if (!pool || pool.tracks.length === 0) return;
    const id = this.next && pool.tracks.includes(this.next) ? this.next : pickDifferent(pool.tracks, this.last);
    const def = MUSIC_TRACKS[id];
    if (!def) return;
    this.retireActive();
    this.last = id;
    this.phase = "playing";
    this.timer = Infinity;
    const gen = ++this.gen;
    void this.prepare(id).then(blob => {
      if (gen === this.gen) this.startStream(id, def, pool, blob);
    }).catch(() => {
      if (gen === this.gen) { this.phase = "gap"; this.timer = 10; }
    });
  }

  /** A single compressed track cache; no partial file ever becomes audible. */
  private prepare(id: string): Promise<Blob> {
    if (this.download?.id === id) return this.download.ready;
    this.clearDownload();
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 120_000);
    const ready = (async () => {
      const response = await fetch(MUSIC_TRACKS[id].url, { signal: abort.signal, cache: 'force-cache' });
      if (!response.ok) throw new Error(`Music HTTP ${response.status}`);
      const maxBytes = 64 * 1024 * 1024;
      if (Number(response.headers.get('content-length')) > maxBytes) throw new Error('Music exceeds compressed cache budget');
      const blob = await response.blob();
      if (!blob.size || blob.size > maxBytes) throw new Error('Invalid music file size');
      return blob;
    })().finally(() => clearTimeout(timeout));
    this.download = { id, abort, ready };
    void ready.catch(() => { if (this.download?.ready === ready) this.download = null; });
    return ready;
  }

  private clearDownload(): void { this.download?.abort.abort(); this.download = null; }

  /** Decode progressively from a local complete file, never from the network. */
  private startStream(id: string, def: MusicTrackDef, pool: MusicPool, blob: Blob): void {
    const ctx = this.engine.ctx;
    const gen = this.gen;
    const loop = def.loop === true;
    const url = URL.createObjectURL(blob);
    const el = new Audio(url);
    el.preload = "auto";
    el.loop = loop;
    let src: MediaElementAudioSourceNode;
    try {
      src = ctx.createMediaElementSource(el);
    } catch {
      URL.revokeObjectURL(url);
      this.phase = "gap";
      this.timer = 5; // couldn't wire it up — retry after a short gap
      return;
    }
    const gain = ctx.createGain();
    gain.gain.value = 0.0001;
    src.connect(gain);
    gain.connect(this.engine.busInput("music"));
    this.active = { id, gain, media: { el, src, url } };
    this.timer = Infinity;
    const toGap = (): void => {
      if (gen === this.gen && this.active?.media?.el === el) this.endTrack();
    };
    if (!loop) el.onended = toGap;
    el.onerror = toGap; // missing/unsupported file → reschedule (don't hang silent)
    el.onplaying = () => {
      if (gen !== this.gen || this.active?.media.el !== el) return;
      this.timer = loop ? rand(pool.minPlay, pool.maxPlay) : Infinity;
      fadeIn(gain, def.gain ?? 0.8, ctx.currentTime);
    };
    void el.play().catch(() => {
      if (gen !== this.gen || this.active?.media.el !== el) return;
      this.retireActive(); this.phase = 'gap'; this.timer = 5;
    });
  }

  private endTrack(): void {
    const pool = this.pool ? MUSIC_POOLS[this.pool] : null;
    this.retireActive();
    this.phase = "gap";
    this.timer = pool ? rand(pool.minGap, pool.maxGap) : 120;
    this.next = pool ? pickDifferent(pool.tracks,this.last) : null;
    if(this.next)void this.prepare(this.next).catch(()=>{});
  }

  /** Fade the active voice out and hand it to the reaper. */
  private retireActive(): void {
    const v = this.active;
    this.active = null;
    if (!v) return;
    const ctx = this.engine.ctx;
    const now = ctx.currentTime;
    v.gain.gain.cancelScheduledValues(now);
    v.gain.gain.setValueAtTime(Math.max(0.0001, v.gain.gain.value), now);
    v.gain.gain.linearRampToValueAtTime(0.0001, now + FADE_OUT);
    v.media.el.onended = null;
    v.media.el.onerror = null;
    v.media.el.onplaying = null;
    this.retiring.push({ gain: v.gain, until: now + FADE_OUT + 0.15, media: v.media });
  }

  /** Stop + disconnect voices whose fade-out has finished. */
  private reap(): void {
    if (this.retiring.length === 0) return;
    const now = this.engine.ctx.currentTime;
    this.retiring = this.retiring.filter((r) => {
      if (now < r.until) return true;
      try {
        r.media.el.pause();
        r.media.el.removeAttribute('src'); r.media.el.load();
        URL.revokeObjectURL(r.media.url);
      } catch {
        /* ignore */
      }
      try {
        r.media.src.disconnect();
      } catch {
        /* ignore */
      }
      try {
        r.gain.disconnect();
      } catch {
        /* ignore */
      }
      return false;
    });
  }

  stop(): void {
    this.setPool(null);
  }
}

function fadeIn(g: GainNode, to: number, now: number): void {
  g.gain.cancelScheduledValues(now);
  g.gain.setValueAtTime(0.0001, now);
  g.gain.linearRampToValueAtTime(to, now + FADE_IN);
}

function rand(a: number, b: number): number {
  return a + Math.random() * (b - a);
}

function pickDifferent(list: string[], avoid: string | null): string {
  if (list.length === 1) return list[0];
  let id = list[Math.floor(Math.random() * list.length)];
  if (id === avoid) id = list[(list.indexOf(id) + 1) % list.length];
  return id;
}
